import {
  ArrowLeftOutlined,
  DownloadOutlined,
  LeftOutlined,
  PlusOutlined,
  WarningFilled,
} from '@ant-design/icons';
import { useDataQualityTestCRUDActions } from '@mm/state/actions/DataQualityTestCRUDActions';
import { StoreState } from '@mm/state/store-interfaces';
import { Collapse, CollapseProps, ConfigProvider, Divider, Spin } from 'antd';
import classNames from 'classnames';
import { uniqueId } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { BookDemoButton } from '../../../common/buttons/BookDemoButton/BookDemoButton';
import { Logo } from '../../../layout/BotBuilderSidebar/Logo/Logo';
import { OPENSEND_FAQ, RETENTION_FAQ } from './constants';
import styles from './DataQualityResultPage.module.css';

export const DataQualityResultPage = () => {
  const results = useSelector(
    (state: StoreState) => state.dataQualityTest.results,
  );

  const { getResult } = useDataQualityTestCRUDActions();
  const history = useHistory();

  const [busy, setBusy] = useState(false);
  const facebookPageId = window.location.pathname.split('/').pop();
  const url = results?.websiteUrl;
  const competitor =
    results?.sources
      .map(s => s.source)
      .find(s => s === 'opensend' || s === 'retention') || 'opensend';

  const BAR_HEIGHT = 685;
  const CALENDLY_URL =
    'https://calendly.com/d/cmtp-44s-vqw/customers-ai-data-quality-review';

  const mappedSources = results?.sources.map(res => ({
    ...res,
    matchedHeightValueInPx: (BAR_HEIGHT * res.matched) / 100,
  }));
  const competitors = mappedSources?.filter(c => c.source !== 'xray');

  const getItems = (source: string) => {
    const faq = source === 'opensend' ? OPENSEND_FAQ : RETENTION_FAQ;
    const items: CollapseProps['items'] = faq.map(i => ({
      ...i,
      key: uniqueId(),
      showArrow: false,
      extra: <PlusOutlined />,
    }));

    return items;
  };

  const percentages = Array.from({ length: 11 }, (_, i) => i * 10).sort(
    (a, b) => b - a,
  );

  const comparableCompetitor = mappedSources?.find(
    s => s.source === competitor,
  );
  const xraySource = mappedSources?.find(s => s.source === 'xray');

  const fetchResult = async () => {
    if (facebookPageId) {
      setBusy(true);
      await getResult(Number(facebookPageId));
      setBusy(false);
    }
  };

  const onEventScheduled = () => {
    setTimeout(() => history.goBack(), 3000);
  };

  useEffect(() => {
    fetchResult();
  }, []);

  return (
    <Spin spinning={busy}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div
            className={styles.logoWithBackBtn}
            onClick={() => history.goBack()}
          >
            <ArrowLeftOutlined />
            <Logo />
          </div>

          <div className={styles.downloadButton}>
            <DownloadOutlined className={styles.downloadIcon} />
            <BookDemoButton
              show
              className={styles.transparentButton}
              button="Get Full Report"
              onEventScheduled={onEventScheduled}
              calendlyUrl={CALENDLY_URL}
            />
          </div>
        </div>
        {!results?.success && !busy && (
          <div className={styles.emptyResults}>
            To get results contact us at{' '}
            <a
              className={styles.supportLink}
              href="mailto:sales@customers.ai"
              rel="noopener noreferrer"
              target="_blank"
            >
              {' '}
              sales@customers.ai
            </a>
          </div>
        )}
        {results?.success && (
          <>
            <div className={styles.titleContainer}>
              <div className={styles.block}>
                <div className={styles.website}>{url}</div>
                <h1 className={styles.title}>Data Quality Test Report </h1>
              </div>
              <div className={classNames(styles.block, styles.alert)}>
                <div
                  className={classNames(
                    styles.message,
                    styles.redBg,
                    styles.white,
                    styles.textPoppins,
                  )}
                >
                  <WarningFilled className={styles.warningIcon} />
                  Damage to your ESP
                </div>
                <div className={classNames(styles.message)}>
                  <WarningFilled
                    className={classNames(
                      styles.warningIcon,
                      styles.font20,
                      styles.redcolor,
                    )}
                  />
                  <span>
                    <span style={{ textTransform: 'capitalize' }}>
                      {comparableCompetitor?.source}
                    </span>{' '}
                    is identifying{' '}
                    <strong>
                      {100 - (comparableCompetitor?.matched || 0)}% of wrong
                      emails.
                    </strong>
                  </span>
                </div>
                <div className={classNames(styles.message)}>
                  <WarningFilled
                    className={classNames(
                      styles.warningIcon,
                      styles.font20,
                      styles.redcolor,
                    )}
                  />
                  <span>
                    <strong>75% less emails landing</strong> in the correct
                    inbox.
                  </span>
                </div>
              </div>
            </div>
            <div className={styles.content}>
              <div className={styles.title}>
                Comparing <span className={styles.website}>{url}</span> actual
                form fill data vs. predicted identities from{' '}
                <span className={styles.website}>Customers.ai</span> vs{' '}
                {competitors?.map((competitor, idx) => (
                  <React.Fragment key={competitor.source}>
                    <span
                      style={{ fontWeight: 400, textTransform: 'capitalize' }}
                    >
                      {competitor.source}
                    </span>
                    <span style={{ fontWeight: 400 }}>
                      .com {idx !== competitors.length - 1 && 'vs'}
                    </span>{' '}
                  </React.Fragment>
                ))}
              </div>
              <div className={styles.chart}>
                <div
                  className={classNames(styles.linesBg, {
                    [styles.linesBgWide]: (competitors?.length || 0) > 1,
                  })}
                >
                  {percentages.map(v => (
                    <Divider
                      orientation="left"
                      plain
                      key={v}
                      className={styles.divider}
                    >
                      {v}%
                    </Divider>
                  ))}
                </div>
                <div className={styles.bars}>
                  <div className={classNames(styles.barWrapper)}>
                    <Logo className={styles.logo} />
                    <div className={styles.bar} style={{ height: BAR_HEIGHT }}>
                      <div
                        className={styles.greenBar}
                        style={{
                          height: xraySource?.matchedHeightValueInPx,
                        }}
                      >
                        {' '}
                        <span className={styles.percent}>
                          {xraySource?.matched}%
                        </span>
                      </div>
                      <div className={styles.redBar}>
                        <span className={styles.percent}>
                          {100 - (xraySource?.matched || 0)}%
                        </span>
                      </div>
                    </div>
                  </div>
                  {competitors?.map((competitor, idx) => {
                    const competitorMismatched =
                      100 - (competitor?.matched || 0);
                    return (
                      <div
                        className={classNames(styles.barWrapper, styles.grayBg)}
                        key={competitor.source}
                      >
                        <div className={styles.textPoppins}>
                          <span style={{ textTransform: 'capitalize' }}>
                            {competitor.source}
                          </span>
                          .com
                        </div>
                        <div
                          className={styles.bar}
                          style={{ height: BAR_HEIGHT }}
                        >
                          <div
                            className={styles.greenBar}
                            style={{
                              height: competitor?.matchedHeightValueInPx,
                            }}
                          >
                            <span className={styles.percent}>
                              {competitor?.matched}%
                            </span>
                          </div>
                          <div className={styles.redBar}>
                            <span className={styles.percent}>
                              {competitorMismatched}%
                            </span>
                            {idx === competitors.length - 1 && (
                              <div className={styles.alertContainer}>
                                <div className={styles.alert}>
                                  <div
                                    className={classNames(
                                      styles.message,
                                      styles.redBg,
                                      styles.white,
                                      styles.textPoppins,
                                    )}
                                  >
                                    <WarningFilled
                                      className={styles.warningIcon}
                                    />
                                    Alert
                                  </div>
                                  <div className={styles.textPoppins}>
                                    Over{' '}
                                    <span className={styles.redcolor}>
                                      {competitorMismatched}% of the emails
                                    </span>{' '}
                                    identified by competitor were{' '}
                                    <span className={styles.redcolor}>
                                      incorrect
                                    </span>
                                  </div>
                                </div>
                                <BookDemoButton
                                  show
                                  className={styles.button}
                                  onEventScheduled={onEventScheduled}
                                  button="Improve Email Identification"
                                  calendlyUrl={CALENDLY_URL}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className={styles.legend}>
                  <div className={styles.legendItem}>
                    <span className={styles.rectangle} />
                    <span className={styles.legendText}>Exact match</span>
                  </div>
                  <div className={styles.legendItem}>
                    <span
                      className={classNames(styles.rectangle, styles.redBg)}
                    />
                    <span className={styles.legendText}>Incorrect match</span>
                  </div>
                </div>
              </div>
              <BookDemoButton
                show
                className={styles.button}
                onEventScheduled={onEventScheduled}
                button="Get Full Damage Report"
                calendlyUrl={CALENDLY_URL}
              />
            </div>
            {competitors?.map(c => (
              <div className={styles.faq} key={c.source}>
                <h3 className={styles.title}>
                  Customers.ai vs{' '}
                  <span style={{ textTransform: 'capitalize' }}>
                    {c.source}
                  </span>
                  .com FAQs
                </h3>
                <ConfigProvider
                  theme={{
                    components: {
                      Collapse: {
                        fontSize: 17,
                        headerBg: 'white',
                      },
                    },
                  }}
                >
                  <Collapse
                    items={getItems(c.source)}
                    className={styles.collapse}
                  />
                </ConfigProvider>
              </div>
            ))}
          </>
        )}
      </div>
    </Spin>
  );
};
